
import { useEffect, useState, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import LoginFormContent from "@/lib/auth/LoginFormContent";
import RegisterFormContent from "@/lib/auth/RegisterFormContent";
import ResetPasswordForm from "@/lib/auth/ResetPasswordForm";
import GoogleLoginButton from "@/lib/auth/GoogleLoginButton";
import GoogleRegisterButton from "@/lib/auth/GoogleRegisterButton";
import { TRAVEL_VIDEOS } from "@/components/utils/assets";
import { cn } from "@/components/utils/utils";
import { useI18n } from '@/hooks/useI18n';

const Login = () => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Travel videos for background
  const travelVideos = TRAVEL_VIDEOS;

  // Handle video end to cycle to next video
  const handleVideoEnd = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % travelVideos.length);
      setIsTransitioning(false);
    }, 500);
  };

  // Create refs for video elements
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // Effect to handle video switching and auto-play
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentVideoIndex) {
          // Start the current video
          video.currentTime = 0;
          video.play().catch(console.error);
        } else {
          // Pause other videos
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [currentVideoIndex]);

  // Effect to start the first video when component mounts
  useEffect(() => {
    if (videoRefs.current[0]) {
      videoRefs.current[0].play().catch(console.error);
    }
  }, []);
  const { t } = useI18n();

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left side - Hero Video Background (Hidden on mobile) */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          {travelVideos.map((video, index) => (
            <video
              key={`${video}-${index}`}
              ref={(el) => (videoRefs.current[index] = el)}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${index === currentVideoIndex ? 'opacity-100' : 'opacity-0'
                }`}
              muted
              loop={false}
              playsInline
              onEnded={index === currentVideoIndex ? handleVideoEnd : undefined}
            >
              <source src={video} type="video/mp4" />
            </video>
          ))}

          {/* Transition overlay for smooth crossfade */}
          {isTransitioning && (
            <div className="absolute inset-0 bg-black/20 transition-opacity duration-500 ease-in-out" />
          )}
        </div>

        {/* Dark Overlay with Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#030A14]/80 via-[#0A1525]/70 to-[#142536]/60"></div>

        {/* Hero Content */}
        <div className="relative z-10 flex flex-col justify-center items-center h-full w-full px-12 lg:px-16 text-center">
          <div className="max-w-2xl">

            {/* Hero Text */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-10">
                {t('login.heroTitle')}
                <span className="block bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                  {t('login.heroTitleColored')}
                </span>
              </h1>

              <p className="text-md lg:text-lg text-white/90 mb-20 max-w-lg mx-auto">
                {t('login.heroSubtitle')}
              </p>
            </div>

          </div>
        </div>

        {/* Bottom Gradient Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#030A14] to-transparent"></div>
      </div>

      {/* Right side - Login Form Sidebar (Full width on mobile) */}
      <div className={cn(
        "bg-white shadow-2xl flex flex-col h-screen",
        "w-full lg:max-w-md"
      )}>
        <div className="flex flex-col h-full">

          {/* Fixed Header with Title and Tabs */}
          <div className="px-4 lg:px-6 pt-4 lg:pt-6 pb-4 border-b border-gray-100">
            {showResetPassword ? (
              /* Reset Password Header */
              <div className="text-center">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                  {t('login.resetTitle')}
                </h2>
                <p className="text-sm text-gray-600">
                  {t('login.resetDesc')}
                </p>
              </div>
            ) : (
              /* Login/Register Header */
              <>
                {/* Title and Subtitle */}
                <div className="mb-6 flex justify-center">
                  <img
                    src="/assets/images/dark_extended.svg"
                    alt="Place2Be Logo"
                    className="w-40 lg:w-48 h-12 lg:h-16 object-contain drop-shadow-2xl"
                  />
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-gray-50">
                    <TabsTrigger value="login" className="text-sm font-medium">
                      {t('auth.login')}
                    </TabsTrigger>
                    <TabsTrigger value="register" className="text-sm font-medium">
                      {t('auth.signUp')}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </>
            )}
          </div>

          {/* Google Button - Only visible when not in reset password mode */}
          {!showResetPassword && (
            <div className="px-4 lg:px-6 py-4 border-b border-gray-100">
              {activeTab === "login" ? <GoogleLoginButton /> : <GoogleRegisterButton />}
              <div className="relative mt-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">
                    {activeTab === "login" ? t('login.orWithEmail') : t('login.orFillForm')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Scrollable Content Area */}
          <ScrollArea className="flex-1 px-4 lg:px-6 py-4">
            {showResetPassword ? (
              <ResetPasswordForm onBackToLogin={() => setShowResetPassword(false)} />
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsContent value="login" className="mt-0">
                  <LoginFormContent onShowResetPassword={() => setShowResetPassword(true)} />
                </TabsContent>

                <TabsContent value="register" className="mt-0">
                  <RegisterFormContent />
                </TabsContent>
              </Tabs>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="px-4 lg:px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-500 text-center leading-relaxed">
              {t('login.footerAgree')}{" "}
              <a href="/#/terms" className="text-[#030A14] hover:underline font-medium">
                {t('login.terms')}
              </a>{" "}
              e la{" "}
              <a href="/#/privacy" className="text-[#030A14] hover:underline font-medium">
                {t('login.privacy')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
